import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useRef } from "react";
import { groupBy } from "@repo/utils";
import { useTranslation } from "react-i18next";

import type { CartItem } from "~/providers/cart-provider";
import { cn } from "~/lib/utils";
import { DataTable } from "../miscellaneous/data-table";

export default function ItemsTable({
  items,
  columns,
  onSelect,
}: {
  items: CartItem[];
  columns: ColumnDef<CartItem>[];
  onSelect?: (item: CartItem) => void;
}) {
  const { t } = useTranslation("common");
  const currentCount = useRef(0);

  const itemsCount = items.reduce((acc, item) => acc + item.quantity, 0);

  const cartTableRef = useRef<HTMLDivElement>(null);

  const sortedItems = useMemo(() => {
    return items.slice().sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        console.error("Invalid date found:", a.createdAt, b.createdAt);
        return 0;
      }

      return dateA.getTime() - dateB.getTime();
    });
  }, [items]);

  const groupedItems = useMemo(
    () => groupBy(sortedItems, "reference"),
    [sortedItems],
  );

  const combinedItemsArray = useMemo(() => {
    return Object.values(groupedItems).map((group) =>
      group.reduce<CartItem>((acc, item, index) => {
        if (index === 0) {
          acc = { ...item };
        } else {
          acc.quantity += item.quantity;
        }
        return acc;
      }, group[0]),
    );
  }, [groupedItems]);
  // console.log(items);

  useEffect(() => {
    const scrollToBottom = () => {
      if (cartTableRef.current) {
        const { scrollHeight, clientHeight } = cartTableRef.current;

        if (scrollHeight > clientHeight + 16) {
          cartTableRef.current.scrollTo({
            top: scrollHeight,
            behavior: "smooth", // Optional: Adds smooth scrolling
          });
        }
      }
    };

    if (itemsCount > currentCount.current) {
      setTimeout(scrollToBottom, 0);
    }
    currentCount.current = itemsCount;
  }, [itemsCount, currentCount]);

  return (
    <ul className={cn("h-[300px] flex-1 bg-card")}>
      <DataTable
        emptyPlaceHolder={t("Cart.empty_placeholder")}
        ref={cartTableRef}
        onSelect={onSelect}
        className="rounded-none border-0"
        borders={true}
        showHeader={false}
        showPagination={false}
        columns={columns}
        data={combinedItemsArray}
        rowLimit={10000}
      />
    </ul>
  );
}
